import { useEffect, useRef, useState } from 'react';
import { useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { getInquiryFilesStatus } from '@/api/inquiries';
import { useChangeCount } from '@/hooks/useChangeCount';
import type { InquiryFileStatus } from '@/types/chat/chat';
import {
  INQUIRY_FILES_POLL_INTERVAL,
  finishedFilesKey,
  shouldPollInquiryFilesAgain,
} from '../inquiryFileProcessing';
import { excludeRemovedFileIds } from '../removeFileFromInquiry';

type FilesStatusKey = readonly [string, string, string, number];

interface UseFileStatusOptions {
  inquiryId: string | undefined;
  /** Any change starts the polling over, as a new inquiry does. */
  reloadData: unknown;
  reloadInquiry: unknown;
  removedFileIdsFor: (inquiryId: string) => Set<string>;
  /**
   * Reload the inquiry after the set of finished files changed; resolves
   * true when the reload was applied. `stillWanted` is false once this
   * polling run has been replaced.
   */
  refreshInquiry: (
    inquiryId: string,
    stillWanted: () => boolean,
  ) => Promise<boolean>;
}

/**
 * The Files strip: GET /chat/inquiry/files-status/{id} at once, then every
 * 30 s while a file is pending (see shouldPollInquiryFilesAgain). A failed
 * first request opens the "Upload failed" modal; any failed request stops
 * the polling until the next start.
 */
export const useFileStatus = ({
  inquiryId,
  reloadData,
  reloadInquiry,
  removedFileIdsFor,
  refreshInquiry,
}: UseFileStatusOptions) => {
  const [files, setFiles] = useState<InquiryFileStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadFailedOpen, setUploadFailedOpen] = useState(false);
  const finishedKeyRef = useRef('');
  const filesInquiryIdRef = useRef<string | null>(null);
  const run = useChangeCount([inquiryId, reloadData, reloadInquiry]);
  const runRef = useRef(run);
  runRef.current = run;
  const startedRunRef = useRef<number | null>(null);
  const pollingRunRef = useRef<number | null>(null);

  useEffect(() => {
    finishedKeyRef.current = '';
  }, [inquiryId]);

  // Declared before the query so it runs before the first request.
  useEffect(() => {
    if (!inquiryId) return;
    if (filesInquiryIdRef.current !== inquiryId) {
      filesInquiryIdRef.current = inquiryId;
      setFiles([]);
    }
    setUploadFailedOpen(false);
  }, [run]);

  const pollFiles = async ({
    queryKey,
  }: QueryFunctionContext<FilesStatusKey>) => {
    const [, , id, keyRun] = queryKey;
    const isInitial = startedRunRef.current !== keyRun;
    startedRunRef.current = keyRun;
    const isCurrent = () => runRef.current === keyRun;

    if (isInitial) setLoading(true);
    const response = await getInquiryFilesStatus(id);
    if (!isCurrent()) return keyRun;
    if (isInitial) setLoading(false);
    pollingRunRef.current = null;

    if (!response.success) {
      if (isInitial) setUploadFailedOpen(true);
      return keyRun;
    }
    if (!response.data) return keyRun;

    setFiles(
      excludeRemovedFileIds(
        response.data,
        removedFileIdsFor(id),
        file => file.id,
      ),
    );

    const finishedKey = finishedFilesKey(response.data);
    const previousKey = finishedKeyRef.current;
    if (finishedKey !== previousKey) {
      void refreshInquiry(id, isCurrent).then(applied => {
        if (applied && isCurrent()) {
          finishedKeyRef.current = finishedKey;
        }
      });
    }
    if (shouldPollInquiryFilesAgain(response.data, previousKey)) {
      pollingRunRef.current = keyRun;
    }
    return keyRun;
  };

  useQuery({
    queryKey: ['chat', 'files-status', inquiryId ?? '', run] as const,
    queryFn: pollFiles,
    enabled: !!inquiryId,
    refetchInterval: query =>
      query.state.fetchStatus === 'idle' &&
      pollingRunRef.current === query.queryKey[3]
        ? INQUIRY_FILES_POLL_INTERVAL
        : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  return {
    files,
    setFiles,
    loading,
    uploadFailedOpen,
    closeUploadFailed: () => setUploadFailedOpen(false),
  };
};
